import type { ContactConsentState } from '../api.js';
import { timestamp } from '../format.js';

/**
 * The deal-submission contact-consent box (Ground Truth §2.4, `gt-v7.17`, #569).
 *
 * Canon's wording is a specification, not a suggestion, and every clause of it is load-bearing:
 *
 * > At submission the client is offered an **explicit, unchecked box — off by default, in plain
 * > language, never pre-selected and never carried in terms of use** — consenting to be contacted
 * > about the deal. … The consent covers **this deal and this contact**. It does not authorize
 * > derivative use. Absent the check, the wall stands exactly as stated above.
 *
 * So, deliberately:
 *
 * - **A real checkbox**, not a toggle whose "off" reads as a setting the user might have changed.
 * - **`checked` is driven by state initialised to `false`** and never seeded from a profile, a prior
 *   deal, or a remembered preference — "never pre-selected" forbids all three. Consent is per-deal.
 * - **Plain language in the label itself**, not behind a link. "Never carried in terms of use" means
 *   the client must be able to read what they are agreeing to without leaving the form.
 * - **The scope is stated where the box is** — this deal, this contact, no derivative use — because a
 *   consent whose limits are stated elsewhere is not the consent canon described.
 *
 * ⚠️ ONE SHARED COMPONENT, used by both submit surfaces, because two copies drift. The wording is
 * canon's obligation and a divergence between doors would be a divergence in what clients were told.
 */

export interface ContactConsentBoxProps {
  /** The client's answer. Always start `false` — never seed it from anywhere. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Disabled while a submission is in flight, like the rest of the form. */
  disabled?: boolean;
}

export function ContactConsentBox({ checked, onChange, disabled }: ContactConsentBoxProps) {
  return (
    <div className="consent">
      <label className="consent__row">
        <input
          type="checkbox"
          className="consent__box"
          // Controlled from state that starts false. NOT `defaultChecked`, which would leave the
          // initial value to the DOM and make "off by default" an assumption rather than a fact.
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled ?? false}
          data-testid="contact-consent"
        />
        <span className="consent__label">You may contact me about this deal.</span>
      </label>
      <p className="consent__scope">
        Optional. Ticking this lets a named Good Good Plans person read this deal and get in touch
        about it. It covers <strong>this deal and this contact only</strong> — it does not add your
        deal to market data, comparable sets, or benchmarks. Leave it unticked and nobody at GGP
        will reach out.
      </p>
    </div>
  );
}

/**
 * ── THE READING HALF ──────────────────────────────────────────────────────────────────────────────
 *
 * What the client answered, shown to the person canon's sentence governs.
 *
 * > Where, and only where, that box is checked, a named GGP person may read the deal and open the
 * > conversation. … **Absent the check, the wall stands exactly as stated above.**
 *
 * That is a permission for a HUMAN, so it has to be legible to one. The answer was recorded from the
 * day the box shipped and read back nowhere — a grant that can only be recovered by querying the
 * database by hand is not a control anybody follows, and the client who ticked the box could not see
 * what they had agreed to either. Both halves are fixed by rendering it.
 *
 * ⚠️ **This DISPLAYS the answer; it does not ENFORCE it.** Blocking a staff read of a non-consented
 * deal waits on per-client tenancy (ADR-0024 D1) — while GGP staff and a client share the `ggp` tenant
 * there is no principal to enforce against. Nothing here should be read as the wall.
 *
 * ⚠️ **It lives in the same file as the box on purpose.** The writer and the reader of a canon
 * obligation drifting apart is exactly how this field spent its whole life unread; anyone editing the
 * question now has the answer's rendering directly beneath it.
 *
 * ## The three states are three different sentences
 *
 * `null` is not a quieter `'declined'`. Collapsing them would either invent a client's refusal or let
 * "we never asked" pass for an answer — and since canon's default is closed, the never-asked case must
 * say the wall stands WITHOUT claiming anybody declined.
 */
const CONSENT_COPY: Record<ContactConsentState | 'unasked', { label: string; note: string }> = {
  granted: {
    label: 'Contact consent given',
    note:
      'A named Good Good Plans person may read this deal and get in touch about it. The consent ' +
      'covers this deal and this contact only — it does not add the deal to market data, ' +
      'comparable sets, or benchmarks.',
  },
  declined: {
    label: 'Contact consent declined',
    note: 'The box was offered at submission and left unticked. Nobody at GGP will reach out about this deal.',
  },
  unasked: {
    label: 'Contact consent not asked',
    // Never asked is OUR omission, and saying "declined" here would put a decision in the client's
    // mouth. It happens on runs the system started (a connector-synced re-analysis has no client to
    // ask), on MCP-created runs, and on runs predating the box. Canon's default is closed, so the
    // wall stands regardless — stated without attributing a refusal to anyone.
    note:
      'No answer was recorded — this run was not started from a submission form that offers the ' +
      'box. Treat it as no consent: nobody at GGP may reach out about this deal.',
  },
};

export interface ContactConsentRecordProps {
  /** The recorded answer. `null` / absent is the real "never asked" state, not missing data. */
  state?: ContactConsentState | null;
  /** When it was answered. Absent exactly when `state` is. */
  at?: string | null;
}

export function ContactConsentRecord({ state, at }: ContactConsentRecordProps) {
  const key = state ?? 'unasked';
  const copy = CONSENT_COPY[key];
  return (
    <section className={`consent-rec consent-rec--${key}`} data-testid="contact-consent-record">
      <p className="consent-rec__head">
        {copy.label}
        {/* The timestamp rides the answer and only the answer. On the never-asked row there is no
            moment to state, and printing the run's own time there would date a decision nobody made. */}
        {at && <span className="consent-rec__when"> · {timestamp(at)}</span>}
      </p>
      <p className="consent-rec__note">{copy.note}</p>
    </section>
  );
}
